/**
 * Reading `fxmanifest.lua` without running it.
 *
 * A manifest is Lua, and the honest way to find out what a Lua file declares is
 * to execute it. That is not an option here: the file comes from a stranger's
 * zip, and a tool that evaluates it to produce a report has handed the report's
 * author a shell.
 *
 * The usual alternative is a pile of regexes, and it is what the previous
 * generation of these tools — ours and everyone else's — actually does. It works
 * until it meets valid syntax it did not anticipate, and then it fails silently:
 *
 *   fx_version("cerulean")        -- call syntax, no space
 *   files({ [[data/a.meta]] })    -- long-bracket strings
 *   --[[ files { 'old.meta' } ]]  -- commented out, and matched anyway
 *   client_script "a.lua"         -- double quotes where the regex wanted single
 *
 * Every one of those produces a wrong answer that looks like a right one: a
 * missing `fx_version` on a file that has one, a phantom declaration from inside
 * a comment. So this module lexes the file properly — strings, comments, long
 * brackets, both call forms — and then reads statements off the token stream.
 * It is a hundred lines more than the regexes and it stops being wrong.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * SCOPE — what this deliberately does not do
 *
 * It is not a Lua parser. Expressions, control flow, concatenation, and
 * variables are not evaluated, and a manifest that computes its file list reads
 * as declaring nothing. That is the safe direction to be wrong in: the result is
 * "undeclared", which is a prompt to look, rather than a fabricated declaration.
 * `unparsed` records every statement it could not read, so a report can say so
 * instead of quietly dropping it.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import type {
  ManifestConstraint,
  ManifestEntry,
  ManifestEntryKind,
  ParsedManifest,
} from "./types";

// ─── Limits ──────────────────────────────────────────────────────────────────

/** Cap the lex so a pathological manifest cannot stall the UI thread. */
const MAX_MANIFEST_BYTES = 1024 * 1024;

/** Cap the statements read, for the same reason. Real manifests are under 100. */
const MAX_STATEMENTS = 5000;

// ─── Lexer ───────────────────────────────────────────────────────────────────

type TokenType = "name" | "string" | "punct" | "other";

type Token = {
  type: TokenType;
  /** For strings, the decoded contents; otherwise the raw text. */
  value: string;
  start: number;
  end: number;
};

const NAME_START = /[A-Za-z_]/;
const NAME_PART = /[A-Za-z0-9_]/;

/**
 * Turn manifest source into tokens, discarding comments and whitespace.
 *
 * Long brackets are the subtle part. `[[text]]`, `[=[text]=]` and `[==[text]==]`
 * are all the same string with different delimiters, and `--[[text]]` is the
 * same thing as a comment. The level (the run of `=` signs) has to match on both
 * ends, which is exactly why authors use the longer forms: to put `]]` inside a
 * string.
 */
function lex(source: string): Token[] {
  const tokens: Token[] = [];
  const limit = Math.min(source.length, MAX_MANIFEST_BYTES);
  let i = 0;

  while (i < limit) {
    const char = source[i];

    // Whitespace.
    if (char === " " || char === "\t" || char === "\r" || char === "\n") {
      i += 1;
      continue;
    }

    // Comments: `--[[ long ]]` or `-- to end of line`.
    if (char === "-" && source[i + 1] === "-") {
      const long = readLongBracket(source, i + 2, limit);
      if (long) {
        i = long.end;
        continue;
      }
      while (i < limit && source[i] !== "\n") i += 1;
      continue;
    }

    // Long-bracket string.
    if (char === "[") {
      const long = readLongBracket(source, i, limit);
      if (long) {
        tokens.push({ type: "string", value: long.value, start: long.start, end: long.end });
        i = long.end;
        continue;
      }
    }

    // Quoted string.
    if (char === "'" || char === '"') {
      const quoted = readQuoted(source, i, limit);
      tokens.push({ type: "string", value: quoted.value, start: i, end: quoted.end });
      i = quoted.end;
      continue;
    }

    // Identifier.
    if (NAME_START.test(char)) {
      const start = i;
      i += 1;
      while (i < limit && NAME_PART.test(source[i])) i += 1;
      tokens.push({ type: "name", value: source.slice(start, i), start, end: i });
      continue;
    }

    // Punctuation we care about; everything else is noise we skip past.
    if ("{}(),=".includes(char)) {
      tokens.push({ type: "punct", value: char, start: i, end: i + 1 });
      i += 1;
      continue;
    }

    tokens.push({ type: "other", value: char, start: i, end: i + 1 });
    i += 1;
  }

  return tokens;
}

/** `[[`, `[=[`… through the matching close. `null` when this is not one. */
function readLongBracket(
  source: string,
  at: number,
  limit: number,
): { value: string; start: number; end: number } | null {
  if (source[at] !== "[") return null;
  let level = 0;
  let i = at + 1;
  while (i < limit && source[i] === "=") {
    level += 1;
    i += 1;
  }
  if (source[i] !== "[") return null;
  const bodyStart = i + 1;
  const closer = `]${"=".repeat(level)}]`;
  const close = source.indexOf(closer, bodyStart);
  // An unterminated long bracket runs to the end of the file, which is what Lua
  // would do too — and which makes the rest of the manifest read as string
  // content rather than as declarations. Reporting nothing is correct here.
  const end = close === -1 ? limit : close + closer.length;
  const body = source.slice(bodyStart, close === -1 ? limit : close);
  return { value: body.replace(/^\r?\n/, ""), start: at, end };
}

const ESCAPES: Record<string, string> = {
  n: "\n",
  t: "\t",
  r: "\r",
  "\\": "\\",
  "'": "'",
  '"': '"',
};

function readQuoted(source: string, at: number, limit: number): { value: string; end: number } {
  const quote = source[at];
  let out = "";
  let i = at + 1;
  while (i < limit) {
    const char = source[i];
    if (char === "\\") {
      const next = source[i + 1];
      out += ESCAPES[next] ?? next ?? "";
      i += 2;
      continue;
    }
    if (char === quote) return { value: out, end: i + 1 };
    // An unterminated quote ends at the newline rather than eating the file.
    if (char === "\n") return { value: out, end: i };
    out += char;
    i += 1;
  }
  return { value: out, end: limit };
}

/**
 * Blank out Lua comments, keeping every other byte where it was.
 *
 * Anything that scans a Lua file for a pattern — `AddTextEntry`, an `exports`
 * call, a `GetResourceState` — has to do this first, or a commented-out line
 * becomes a declaration the resource does not actually make. The naive version
 * replaces everything from a `--` to the end of the line, and it is wrong in
 * both directions: it eats a `--` that appears inside a string, and it misses
 * `--[==[` long comments entirely.
 *
 * Comments are replaced with spaces rather than removed so that every byte
 * offset in the result still refers to the same byte of the original. A finding
 * that reports a line number computed against a shortened copy of the file
 * points at the wrong line, which is worse than reporting no line at all.
 */
export function stripLuaComments(source: string): string {
  const limit = Math.min(source.length, MAX_MANIFEST_BYTES);
  const out = source.split("");
  let i = 0;

  const blank = (from: number, to: number) => {
    for (let j = from; j < to && j < out.length; j += 1) {
      if (out[j] !== "\n") out[j] = " ";
    }
  };

  while (i < limit) {
    const char = source[i];

    if (char === "'" || char === '"') {
      i = readQuoted(source, i, limit).end;
      continue;
    }

    if (char === "[") {
      const long = readLongBracket(source, i, limit);
      if (long) {
        i = long.end;
        continue;
      }
    }

    if (char === "-" && source[i + 1] === "-") {
      const long = readLongBracket(source, i + 2, limit);
      if (long) {
        blank(i, long.end);
        i = long.end;
        continue;
      }
      let end = i;
      while (end < limit && source[end] !== "\n") end += 1;
      blank(i, end);
      i = end;
      continue;
    }

    i += 1;
  }

  return out.join("");
}

// ─── Key mapping ─────────────────────────────────────────────────────────────

/**
 * Manifest keys, singular and plural, mapped to what they mean.
 *
 * FiveM accepts both forms of every list key and treats them identically, so a
 * checker that only knows `client_scripts` reports a resource using
 * `client_script` as having no client code at all.
 */
const KEYS: Record<string, ManifestEntryKind> = {
  fx_version: "fx_version",
  game: "game",
  games: "game",
  client_script: "client_script",
  client_scripts: "client_script",
  server_script: "server_script",
  server_scripts: "server_script",
  shared_script: "shared_script",
  shared_scripts: "shared_script",
  file: "file",
  files: "file",
  ui_page: "ui_page",
  data_file: "data_file",
  dependency: "dependency",
  dependencies: "dependency",
  provide: "provide",
  provides: "provide",
  // Not paths but names a resource offers to other resources. Reading them as
  // paths would report every export as a missing file.
  export: "export",
  exports: "export",
  server_export: "server_export",
  server_exports: "server_export",
  escrow_ignore: "escrow_ignore",
  lua54: "lua54",
  author: "author",
  description: "description",
  version: "version",
  this_is_a_map: "this_is_a_map",
  server_only: "server_only",
};

/** Keys that take a type and then a path, rather than a list of paths. */
const QUALIFIED_KEYS = new Set(["data_file"]);

// ─── Line index ──────────────────────────────────────────────────────────────

/**
 * Offsets of every line start, so a span's line and column are a binary search
 * rather than a scan. Built once per parse; a manifest with 400 declarations
 * would otherwise walk the file 400 times.
 */
function lineStarts(source: string): number[] {
  const starts = [0];
  for (let i = 0; i < source.length; i += 1) {
    if (source.charCodeAt(i) === 10) starts.push(i + 1);
  }
  return starts;
}

function lineColumn(starts: number[], offset: number): { line: number; column: number } {
  let low = 0;
  let high = starts.length - 1;
  while (low < high) {
    const mid = (low + high + 1) >> 1;
    if (starts[mid] <= offset) low = mid;
    else high = mid - 1;
  }
  return { line: low + 1, column: offset - starts[low] + 1 };
}

// ─── Parser ──────────────────────────────────────────────────────────────────

/**
 * A Cfx constraint, not a resource.
 *
 * `dependencies { 'ox_lib', '/server:6683' }` mixes two unrelated things in one
 * block: a resource this needs, and a server build this needs. Treating the
 * second as a resource name produces the memorable false positive "dependency
 * '/server:6683' is missing" — and, worse, hides the real information, which is
 * that this resource will not start on an older server.
 */
const CONSTRAINT = /^\/([A-Za-z_][A-Za-z0-9_]*):(.*)$/;

/**
 * Parse a manifest into declarations.
 *
 * Never throws. A file that is not a manifest at all comes back with no entries
 * and everything it saw in `unparsed`.
 */
export function parseManifest(fileName: string, source: string): ParsedManifest {
  const tokens = lex(source);
  const starts = lineStarts(source);
  const entries: ManifestEntry[] = [];
  const constraints: ManifestConstraint[] = [];
  const unparsed: string[] = [];

  let fxVersion: string | null = null;
  const games: string[] = [];
  let legacy = fileName.toLowerCase() === "__resource.lua";

  const spanOf = (token: Token) => ({
    file: fileName,
    byteStart: token.start,
    byteEnd: token.end,
    ...lineColumn(starts, token.start),
  });

  let i = 0;
  let statements = 0;
  while (i < tokens.length && statements < MAX_STATEMENTS) {
    const token = tokens[i];
    if (token.type !== "name") {
      i += 1;
      continue;
    }
    statements += 1;

    const key = token.value.toLowerCase();
    if (key === "resource_manifest_version") legacy = true;

    const kind = KEYS[key];
    i += 1;

    // Skip the optional `=` or `(` that separates a key from its value. Both
    // `files { … }` and `files({ … })` and `files = { … }` are the same
    // declaration and all three appear in the wild.
    let closeParen = false;
    if (tokens[i]?.type === "punct" && tokens[i].value === "=") i += 1;
    if (tokens[i]?.type === "punct" && tokens[i].value === "(") {
      closeParen = true;
      i += 1;
    }

    const values: Token[] = [];
    let depth = 0;
    while (i < tokens.length) {
      const next = tokens[i];
      if (next.type === "punct") {
        if (next.value === "{") {
          depth += 1;
          i += 1;
          continue;
        }
        if (next.value === "}") {
          depth -= 1;
          i += 1;
          if (depth <= 0 && !closeParen) break;
          continue;
        }
        if (next.value === ")") {
          i += 1;
          if (closeParen) break;
          continue;
        }
        if (next.value === ",") {
          i += 1;
          continue;
        }
        // `=` inside a table is a keyed field; the key is not a path.
        if (next.value === "=") {
          values.pop();
          i += 1;
          continue;
        }
        i += 1;
        continue;
      }
      if (next.type === "string") {
        values.push(next);
        i += 1;
        // A bare `key 'value'` with no braces and no parens ends after its
        // values; the next name token starts the next statement.
        if (depth === 0 && !closeParen && tokens[i]?.type === "name") break;
        continue;
      }
      if (next.type === "name") {
        // A name inside a table or an argument list is a Lua expression we do
        // not evaluate; a name at depth 0 is the next statement.
        if (depth === 0 && !closeParen) break;
        i += 1;
        continue;
      }
      i += 1;
    }

    if (!kind) {
      if (values.length > 0) unparsed.push(`${token.value}: ${values.map((v) => v.value).join(", ")}`);
      continue;
    }

    if (kind === "fx_version") {
      if (values[0]) fxVersion = values[0].value.trim();
    }
    if (kind === "game") {
      for (const value of values) games.push(value.value.trim().toLowerCase());
    }

    if (QUALIFIED_KEYS.has(key)) {
      // `data_file 'TYPE' 'path'`, possibly repeated inside one statement.
      for (let pair = 0; pair + 1 < values.length; pair += 2) {
        entries.push({
          kind,
          key: token.value,
          qualifier: values[pair].value.trim(),
          value: values[pair + 1].value.trim(),
          span: spanOf(values[pair + 1]),
        });
      }
      if (values.length === 1) {
        unparsed.push(`${token.value} with one argument: ${values[0].value}`);
      }
      continue;
    }

    for (const value of values) {
      const text = value.value.trim();
      if (text === "") continue;
      const constraint = CONSTRAINT.exec(text);
      if (constraint) {
        constraints.push({
          kind: constraint[1],
          value: constraint[2],
          raw: text,
          span: spanOf(value),
        });
        continue;
      }
      entries.push({ kind, key: token.value, value: text, span: spanOf(value) });
    }
  }

  return {
    file: fileName,
    legacy,
    fxVersion,
    games,
    entries,
    constraints,
    unparsed,
  };
}

// ─── Reading a parsed manifest ───────────────────────────────────────────────

/** Every declared value of one kind, in declaration order. */
export function entriesOfKind(manifest: ParsedManifest, kind: ManifestEntryKind): ManifestEntry[] {
  return manifest.entries.filter((entry) => entry.kind === kind);
}

/** Every path the manifest expects to ship: scripts, files and the UI page. */
export function declaredPaths(manifest: ParsedManifest): ManifestEntry[] {
  return manifest.entries.filter(
    (entry) =>
      entry.kind === "client_script" ||
      entry.kind === "server_script" ||
      entry.kind === "shared_script" ||
      entry.kind === "file" ||
      entry.kind === "ui_page" ||
      entry.kind === "data_file",
  );
}

/**
 * `@ox_lib/init.lua` — a script that lives in *another* resource.
 *
 * This is the single most common false "missing file" in the whole category. The
 * path is real, it is just not in this folder, and FiveM resolves it at load
 * time from the named resource. Reading it as a local path produces an error on
 * a line that is not only correct but is the documented way to use every major
 * library.
 */
export function parseResourceInclude(value: string): { resource: string; path: string } | null {
  if (!value.startsWith("@")) return null;
  const slash = value.indexOf("/");
  if (slash <= 1) return null;
  return { resource: value.slice(1, slash), path: value.slice(slash + 1) };
}

/** Whether a manifest path points outside this resource. */
export function isExternalInclude(value: string): boolean {
  return parseResourceInclude(value) !== null;
}

/**
 * Glob-ish matching for `files { 'data/*.meta' }`.
 *
 * FiveM's globbing is not full glob: `*` matches within a path segment and `**`
 * matches across them, and that is the whole of it. Implementing more would
 * invent behaviour the loader does not have, and a file "declared" by a pattern
 * the game will not expand is a file that does not ship.
 */
/** Characters that mean something to a regular expression and must be escaped. */
const REGEX_SPECIAL = /[.+^${}()|[\]\\]/;

export function manifestPatternToRegExp(pattern: string): RegExp {
  // One left-to-right pass, rather than a chain of replaces. A chain has to park
  // `**` somewhere while it expands `*`, and every placeholder cheap enough to
  // use is either a character a path could legitimately contain or a control
  // character that has no business being inside a regular expression.
  let out = "";
  for (let i = 0; i < pattern.length; i += 1) {
    const char = pattern[i];
    if (char === "*") {
      if (pattern[i + 1] === "*") {
        out += ".*";
        i += 1;
      } else {
        out += "[^/]*";
      }
      continue;
    }
    if (char === "?") {
      out += "[^/]";
      continue;
    }
    out += REGEX_SPECIAL.test(char) ? `\\${char}` : char;
  }
  return new RegExp(`^${out}$`, "i");
}

export function isGlobPattern(value: string): boolean {
  return value.includes("*") || value.includes("?");
}
